/**
 * CopyButton — copies a string to the clipboard with brief visual confirmation.
 *
 * Used where the crew needs the raw value to paste elsewhere (a site address
 * into Waze, an on-site contact number into the dialer). Clipboard access needs
 * a secure context (https / localhost); on failure it silently no-ops.
 */
import { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { cn } from '@/lib/cn';

export function CopyButton({
  value,
  label = 'Copy',
  className,
}: {
  value: string;
  label?: string;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard unavailable (insecure context / denied) — nothing to do.
    }
  }

  return (
    <button
      type="button"
      onClick={copy}
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex items-center justify-center rounded-md p-1.5 text-ink-faint hover:text-ink hover:bg-fill-soft transition-colors',
        className,
      )}
    >
      {copied ? (
        <Check className="w-3.5 h-3.5 text-status-complete-fg" />
      ) : (
        <Copy className="w-3.5 h-3.5" />
      )}
    </button>
  );
}
